import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FileText, X, Loader2 } from "lucide-react";

/**
 * value is a string (the extracted text). To also send the raw file to the
 * backend on submit, wire an onFileMeta callback which receives
 * { filename, file_b64, content_type }.
 */
export default function ResumeUpload({ value, onChange, onFileMeta, testid = "resume-upload" }) {
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(pdf|docx)$/i.test(file.name)) {
      toast.error("Only PDF or DOCX files are accepted");
      e.target.value = "";
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File must be under 5 MB");
      e.target.value = "";
      return;
    }
    setLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await api.post("/util/parse-resume", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(res.data.text);
      onFileMeta && onFileMeta({
        filename: res.data.filename,
        file_b64: res.data.file_b64,
        content_type: res.data.content_type,
      });
      setFileName(file.name);
      toast.success(`Loaded ${file.name} (${res.data.chars} chars)`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to read the file");
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  const clear = () => {
    onChange("");
    onFileMeta && onFileMeta(null);
    setFileName("");
  };

  return (
    <div data-testid={testid}>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        onChange={handleFile}
        data-testid={`${testid}-input`}
      />
      {!value && (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="w-full border-2 border-dashed border-navy/25 hover:border-gold hover:bg-slateBg/40 transition-colors p-8 text-center"
          data-testid={`${testid}-picker`}
        >
          {loading ? (
            <Loader2 size={22} className="animate-spin mx-auto text-gold" />
          ) : (
            <Upload size={22} className="mx-auto text-gold" />
          )}
          <div className="serif-display text-lg mt-3">Upload resume</div>
          <div className="text-xs text-navy/50 mt-1">PDF or DOCX · max 5 MB</div>
        </button>
      )}
      {value && (
        <div className="border border-navy/15 bg-white p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <FileText size={18} className="text-gold shrink-0" />
            <div className="min-w-0">
              <div className="serif-display truncate">{fileName || "Resume uploaded"}</div>
              <div className="text-xs text-navy/50">{value.length.toLocaleString()} characters extracted</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button type="button" className="label-eyebrow text-navy/60 hover:text-gold" onClick={() => inputRef.current?.click()} data-testid={`${testid}-replace`}>Replace</button>
            <button type="button" className="text-navy/40 hover:text-red-600 p-1" onClick={clear} data-testid={`${testid}-clear`}><X size={16}/></button>
          </div>
        </div>
      )}
    </div>
  );
}
